import type {
  ConsentChangeNotification,
  TrackingStorageClient,
} from '../../configParametersRepository/storageClient/types';
import { trackError } from '../../utils/trackError';
import { shouldSkipTracking } from '../utils/cookie';

const CONSENT_BOUNDARY = 'tagCommander/consent';
const receivedNotifications = new WeakSet<ConsentChangeNotification>();
let pendingNotifications = 0;
let isListening = false;

const reportConsentError = (error: unknown) => {
  void trackError(
    error instanceof Error
      ? error
      : new Error(
          typeof error === 'string'
            ? error
            : 'Consent tracking failed with a non-Error value'
        ),
    CONSENT_BOUNDARY
  );
};

export const flushConsentNotifications = (): void => {
  if (!pendingNotifications) return;
  if (shouldSkipTracking()) {
    pendingNotifications = 0;
    return;
  }
  while (
    pendingNotifications &&
    typeof window.tC?.event?.customConsentChange === 'function'
  ) {
    pendingNotifications--;
    try {
      window.tC.event.customConsentChange();
    } catch (error: unknown) {
      // Retrying a throwing TC handler could duplicate a pixel it already sent.
      reportConsentError(error);
    }
  }
};

export const initializeConsentTracking = (
  storage?: TrackingStorageClient | null
): void => {
  flushConsentNotifications();
  if (isListening || !storage) return;
  isListening = true;
  void storage
    .listenChangeUserConsent((notification) => {
      if (
        notification?.regulationName === 'CPRA' &&
        Number.isSafeInteger(notification.notificationId) &&
        notification.notificationId > 0 &&
        !receivedNotifications.has(notification)
      ) {
        // CMP shares the same frozen object for live delivery and replay, even
        // across clients. Numeric IDs alone collide across CMP module instances.
        receivedNotifications.add(notification);
        pendingNotifications++;
      }
      flushConsentNotifications();
    })
    .catch((error: unknown) => {
      isListening = false;
      reportConsentError(error);
    });
};
